import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toPng } from 'html-to-image';
import SafeIcon from '../common/SafeIcon';
import { getDayDiff } from '../utils/dateUtils';
import { getThemeStyle } from '../utils/themeUtils';
import { useLocale } from '../context/LocaleContext';

export default function ShareModal({ isOpen, onClose, item }) {
  const { t } = useLocale();
  const [ratio, setRatio] = useState('1:1');
  const [isExporting, setIsExporting] = useState(false);
  const [scale, setScale] = useState(0.2);
  
  const cardRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setTimeout(() => setRatio('1:1'), 300);
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    
    const updateScale = () => {
      if (!containerRef.current) return;
      const { clientWidth, clientHeight } = containerRef.current;
      
      const cardW = 1080;
      const cardH = ratio === '1:1' ? 1080 : 1920;
      
      const availableW = clientWidth - 40;
      const availableH = clientHeight - 40;
      
      const scaleW = availableW / cardW;
      const scaleH = availableH / cardH;
      
      setScale(Math.max(0.05, Math.min(scaleW, scaleH)));
    };

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(updateScale);
    });
    
    if (containerRef.current) observer.observe(containerRef.current);
    const timeId = setTimeout(updateScale, 50);
    
    return () => {
      observer.disconnect();
      clearTimeout(timeId);
    };
  }, [ratio, isOpen]);

  if (!item) return null;

  const { diffDays, status, weekDayIndex } = getDayDiff(new Date(), item.targetDateISO);
  const themeStyle = getThemeStyle(item.theme, status, false);

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setIsExporting(true);
    
    try {
      // 預熱 html-to-image，避免第一次導出圖片字體遺失或破圖
      await toPng(cardRef.current, { cacheBust: true, pixelRatio: 1 }).catch(() => {});
      await new Promise(res => setTimeout(res, 300));
      
      const dataUrl = await toPng(cardRef.current, { 
        cacheBust: true, 
        pixelRatio: 2,
        style: { transform: 'none', margin: '0' }
      });
      
      const fileName = `DaysMatter_${t(item.title)}.png`;

      // 🌟 Premium Enhancement: 支援原生 Web Share API (針對手機 PWA 優化)
      let shared = false;
      if (navigator.share && navigator.canShare) {
        try {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], fileName, { type: 'image/png' });
          
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'DaysMatter',
              text: t(item.title),
              files: [file]
            });
            shared = true;
          }
        } catch (shareErr) {
          // 如果使用者在中途取消分享 (AbortError)，則不執行後續的備用下載
          if (shareErr.name === 'AbortError') {
            return;
          }
          console.warn('Web Share API 失敗，退回一般下載模式:', shareErr);
        }
      }
      
      // 如果瀏覽器不支援 Web Share API 或分享失敗，退回傳統的 <a> 標籤強制下載
      if (!shared) {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      console.error(err);
      alert(t('share.error'));
    } finally {
      setIsExporting(false);
    }
  };

  const renderCardContent = () => {
    const isSquare = ratio === '1:1';
    
    return (
      <div 
        className={`bg-gradient-to-br ${themeStyle.bg} flex flex-col items-center justify-center relative overflow-hidden bg-white`}
        style={{ width: '1080px', height: isSquare ? '1080px' : '1920px' }}
      >
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjUpIi8+PC9zdmc+')] opacity-50"></div>
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white opacity-40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white opacity-40 rounded-full blur-3xl"></div>
        
        <div className="bg-white/90 backdrop-blur-xl rounded-[4rem] p-16 shadow-2xl w-[80%] flex flex-col items-center relative z-10 border-[12px] border-white">
          <div className="text-8xl mb-8 bg-gray-50 w-40 h-40 flex items-center justify-center rounded-[2.5rem] shadow-inner">
            {item.icon}
          </div>
          
          <h2 className="text-5xl font-black text-gray-800 mb-6 text-center leading-tight">
            {t(item.title)}
          </h2>
          
          <div className="bg-gray-100 rounded-full px-8 py-3 mb-12 flex items-center gap-4">
            {item.category && item.category !== 'other' && (
              <span className="bg-gray-800 text-white px-4 py-1 rounded-full text-xl font-bold">
                {t(`category.${item.category}`)}
              </span>
            )}
            <p className="text-2xl text-gray-600 font-bold">
              {item.targetDateISO} {t(`date.weekdays.${weekDayIndex}`)}
            </p>
          </div>
          
          <div className="text-center mb-8 w-full">
            <span className="text-3xl text-gray-500 font-bold block mb-4">
              {status === 'today' ? t('common.today') : (status === 'remaining' ? t('common.remaining') : t('common.elapsed'))}
            </span>
            {status !== 'today' ? (
              <div className={`flex items-baseline justify-center gap-4 ${themeStyle.text}`}>
                <span className="text-[10rem] font-black leading-none drop-shadow-md">{diffDays}</span>
                <span className="text-5xl font-bold">{t('common.days')}</span>
              </div>
            ) : (
              <span className="text-[8rem] font-black leading-none my-8 block drop-shadow-md">🎉</span>
            )}
          </div>

          {item.note && (
            <p className="text-3xl text-gray-500 font-medium bg-gray-50 px-8 py-6 rounded-3xl mt-4 w-full text-center">
              {t(item.note)}
            </p>
          )}
        </div>

        <div className="absolute bottom-16 flex items-center gap-4 opacity-90">
          <div className="bg-white p-4 rounded-2xl shadow-sm">
            <SafeIcon name="Heart" className={`w-8 h-8 ${themeStyle.text}`} />
          </div>
          <span className="text-4xl font-black text-white drop-shadow-md tracking-wide">
            DaysMatter
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ y: "100%", opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: "100%", opacity: 0, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-[#f8f9fa] dark:bg-slate-900 w-full sm:w-[420px] rounded-t-[2rem] sm:rounded-[2.5rem] shadow-2xl relative z-10 flex flex-col overflow-hidden border border-white/50 dark:border-slate-800"
              style={{ maxHeight: '95vh' }}
            >
              <div className="px-6 py-5 flex justify-between items-center bg-white dark:bg-slate-800 shrink-0">
                <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
                  <SafeIcon name="Image" className="w-5 h-5 text-gray-400" />
                  {t('share.title')}
                </h2>
                <button onClick={onClose} className="p-2 bg-gray-100 dark:bg-slate-700 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors">
                  <SafeIcon name="X" className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-5">
                <div className="bg-gray-200/60 dark:bg-slate-800 p-1.5 rounded-2xl flex gap-1 relative shadow-inner">
                  <button onClick={() => setRatio('1:1')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all z-10 ${ratio === '1:1' ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
                    {t('share.igPost')}
                  </button>
                  <button onClick={() => setRatio('9:16')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm transition-all z-10 ${ratio === '9:16' ? 'text-gray-800 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>
                    {t('share.igStory')}
                  </button>
                  <div className="absolute top-1.5 bottom-1.5 w-[calc(50%-0.375rem)] bg-white dark:bg-slate-600 rounded-xl shadow-sm transition-transform duration-300 ease-out" style={{ transform: ratio === '1:1' ? 'translateX(0)' : 'translateX(calc(100% + 0.25rem))' }} />
                </div>

                <div 
                  ref={containerRef}
                  className="w-full h-[380px] sm:h-[420px] bg-white dark:bg-slate-950 rounded-2xl overflow-hidden relative flex items-center justify-center shadow-inner border dark:border-slate-800"
                  style={{ backgroundImage: 'radial-gradient(rgba(156, 163, 175, 0.3) 2px, transparent 2px)', backgroundSize: '20px 20px' }}
                >
                  <div 
                    className="absolute origin-center shadow-2xl transition-all duration-500 ease-out pointer-events-none"
                    style={{ 
                      width: '1080px', 
                      height: ratio === '1:1' ? '1080px' : '1920px',
                      transform: `scale(${scale})`
                    }}
                  >
                    {renderCardContent()}
                  </div>
                </div>
              </div>

              <div className="p-5 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700/50 shrink-0">
                <button 
                  onClick={handleDownload}
                  disabled={isExporting}
                  className="w-full py-3.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-bold text-lg flex items-center justify-center gap-2 hover:bg-gray-800 dark:hover:bg-gray-100 transition-all active:scale-95 disabled:opacity-70 shadow-lg"
                >
                  {isExporting ? <SafeIcon name="Loader" className="w-5 h-5 animate-spin" /> : <SafeIcon name="Download" className="w-5 h-5" />}
                  {isExporting ? t('share.downloading') : t('share.download')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 隱藏的渲染容器 */}
      {isOpen && (
        <div className="fixed top-[-9999px] left-[-9999px] pointer-events-none">
          <div ref={cardRef}>
            {renderCardContent()}
          </div>
        </div>
      )}
    </>
  );
}